import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Energy Baseline Form for Live Session
 * Collects required energy baseline information before questions can be shown
 */
export const EnergyBaselineForm = ({ session, isConsultant, onComplete }) => {
  const [energyBaseline, setEnergyBaseline] = useState({
    annualKwh: '',
    annualElectricityCost: '',
    averageMonthlyBill: '',
    annualProduction: '',
    productionUnit: '',
    peakDemandKw: '',
    annualGasUsage: '',
    gasUsageUnit: 'therms',
    facilitySize: '',
    operatingHours: '',
    utilityRateType: '',
    hasEnergyMonitoring: '',
    hasHistoricalData: '',
    productionCapacity: '',
    averageProductionRate: '',
    annualRevenue: '',
    productionSeasons: ''
  });
  
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Load existing energy baseline from session notes if available
  useEffect(() => {
    if (session?.notes) {
      try {
        const notesData = typeof session.notes === 'string' ? JSON.parse(session.notes) : session.notes;
        if (notesData.energyBaseline) {
          setEnergyBaseline(prev => ({
            ...prev,
            ...notesData.energyBaseline
          }));
        }
      } catch (e) {
        logger.warn('Could not parse energy baseline from session notes:', e);
      }
    }
  }, [session]);

  const validate = () => {
    const newErrors = {};
    
    // Required fields from Energy Baseline Information
    if (!energyBaseline.annualKwh || !energyBaseline.annualKwh.toString().trim()) {
      newErrors.annualKwh = 'Annual Electricity Usage is required';
    }
    if (!energyBaseline.annualElectricityCost || !energyBaseline.annualElectricityCost.toString().trim()) {
      newErrors.annualElectricityCost = 'Annual Electricity Cost is required';
    }
    if (!energyBaseline.averageMonthlyBill || !energyBaseline.averageMonthlyBill.toString().trim()) {
      newErrors.averageMonthlyBill = 'Average Monthly Utility Bill is required';
    }
    
    // Required fields from Production Information
    if (!energyBaseline.annualProduction || !energyBaseline.annualProduction.toString().trim()) {
      newErrors.annualProduction = 'Annual Production Volume is required';
    }
    if (!energyBaseline.productionUnit || !energyBaseline.productionUnit.trim()) {
      newErrors.productionUnit = 'Production Unit is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      
      // Parse existing notes or create new object
      let notesData = {};
      if (session?.notes) {
        try {
          notesData = typeof session.notes === 'string' ? JSON.parse(session.notes) : session.notes;
        } catch (e) {
          logger.warn('Could not parse existing notes, creating new:', e);
        }
      }
      
      // Add energy baseline to notes
      notesData.energyBaseline = energyBaseline;
      
      // Update session with energy baseline in notes
      const response = await axios.patch(
        `${API_URL}/api/live-session/${session.id}`,
        { notes: JSON.stringify(notesData) },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success('Energy baseline information saved!');
        if (onComplete) {
          onComplete();
        }
      }
    } catch (err) {
      logger.error('Error saving energy baseline:', err);
      toast.error(err.response?.data?.detail || 'Failed to save energy baseline information');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setEnergyBaseline(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Consultant view-only mode
  if (isConsultant) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-300 dark:border-blue-700 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <div>
              <p className="text-blue-900 dark:text-blue-200 font-semibold mb-1">👀 View-Only Mode</p>
              <p className="text-blue-800 dark:text-blue-300 text-sm">
                The user is filling out the energy baseline information. You can view their progress in real-time.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <form onSubmit={handleSubmit}>
        <div className="p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Energy Baseline Information
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Provide your facility&apos;s current energy usage data to enable accurate savings
              calculations and ROI analysis.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Annual Electricity Usage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Annual Electricity Usage (kWh){' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="number"
                value={energyBaseline.annualKwh}
                onChange={(e) => handleChange('annualKwh', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500 ${
                  errors.annualKwh ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="e.g., 500000"
              />
              {errors.annualKwh && (
                <p className="text-xs text-red-500 dark:text-red-400 mt-1">{errors.annualKwh}</p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Check your annual utility bills or 12-month average
              </p>
            </div>

            {/* Annual Electricity Cost */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Annual Electricity Cost ($){' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="number"
                value={energyBaseline.annualElectricityCost}
                onChange={(e) => handleChange('annualElectricityCost', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500 ${
                  errors.annualElectricityCost ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="e.g., 75000"
              />
              {errors.annualElectricityCost && (
                <p className="text-xs text-red-500 dark:text-red-400 mt-1">{errors.annualElectricityCost}</p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Total annual electricity expenses including all charges
              </p>
            </div>

            {/* Average Monthly Bill */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Average Monthly Utility Bill ($){' '}
                <span className="text-red-500 dark:text-red-400">*</span>
              </label>
              <input
                type="number"
                value={energyBaseline.averageMonthlyBill}
                onChange={(e) => handleChange('averageMonthlyBill', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500 ${
                  errors.averageMonthlyBill ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="e.g., 6250"
              />
              {errors.averageMonthlyBill && (
                <p className="text-xs text-red-500 dark:text-red-400 mt-1">{errors.averageMonthlyBill}</p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Average monthly electricity bill amount
              </p>
            </div>

            {/* Peak Demand */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Peak Demand (kW)
              </label>
              <input
                type="number"
                value={energyBaseline.peakDemandKw}
                onChange={(e) => handleChange('peakDemandKw', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
                placeholder="e.g., 150"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Maximum demand from utility bills (if available)
              </p>
            </div>
          </div>

          {/* Production Information Section */}
          <div className="mt-8">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Production Information
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Help us calculate your true energy productivity by providing production data. This
              enables accurate benchmarking and savings calculations.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Annual Production Volume */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Annual Production Volume{' '}
                  <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="number"
                  value={energyBaseline.annualProduction}
                  onChange={(e) => handleChange('annualProduction', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500 ${
                    errors.annualProduction ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="e.g., 50000"
                />
                {errors.annualProduction && (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-1">{errors.annualProduction}</p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Total units produced annually
                </p>
              </div>

              {/* Production Unit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Production Unit <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <select
                  value={energyBaseline.productionUnit}
                  onChange={(e) => handleChange('productionUnit', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500 ${
                    errors.productionUnit ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <option value="">Select unit</option>
                  <option value="tons">Tons</option>
                  <option value="cases">Cases</option>
                  <option value="units">Units</option>
                  <option value="pounds">Pounds</option>
                  <option value="gallons">Gallons</option>
                  <option value="liters">Liters</option>
                  <option value="pieces">Pieces</option>
                  <option value="batches">Batches</option>
                  <option value="other">Other</option>
                </select>
                {errors.productionUnit && (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-1">{errors.productionUnit}</p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Unit of measurement for production
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Continue to Assessment Questions'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

